import API from '../../utils/API';
import DateRangePicker from '../DateRangePicker';
import SlideToggle from '../SlideToggle';
import UnitToggle from '../UnitToggle';
import './style.css';
import GlobalFuncs from '../../utils/GlobalFuncs';
import GraphFuncs from '../../utils/GraphFuncs';
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AppContext } from '../../utils/ContextProvider';
import Loader from '../../components/Loader';
import GlobalSVG from '../../utils/GlobalSVG';
import DistribuitionTable from '../DistributionTable';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import moment from 'moment-timezone';
import Tooltip from '../../components/ToolTip';

// Register Highcharts modules once, when this file is first loaded (not on every render).
highchartsExporting(Highcharts);
highchartsExportData(Highcharts);


/* ==========================================================================
   CONSTANTS
   ========================================================================== */

const COLORS = ['#a3d237', '#4fa78b', '#006184', '#6e2b68', '#ffcf00', '#ff7500', '#db0661', '#007b4e', '#588C9E', '#007b4e'];

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const DAILY_DETAILS = ['day', 'month'];

// Order matters: series are requested (and drawn) in this order.
const HARMONIC_UNITS = ['unblv', 'unbli', 'thdva', 'thdv', 'othdv', 'ethdv', 'thffv', 'cfv', 'kfi', 'ethdi', 'othdi', 'thdi'];
const ELECTRIC_UNITS = ['kw', 'kva', 'kvar', 'pf', 'a', 'vll', 'vln', ...HARMONIC_UNITS, 'co'];
const HARMONICS_OFF = Object.fromEntries(HARMONIC_UNITS.map((unit) => [unit, false]));

// Maps a circuit's raw input_unit to the unit used for water/gas series.
const DIGITAL_UNIT_LABELS = { 'l.': 'L', 'ft^3': 'ft³' };

// Numeric utility ids expected by the alerts / maintenance endpoints.
const EVENT_TYPE_IDS = { electricity: 0, gas: 1, water: 2 };

// Which shared y-axis ("Consumption" vs "Digital / Power Quality") a unit belongs to.
const UNIT_AXIS_GROUP = {
    kva: 'electricity', kw: 'electricity', kwh: 'electricity', kvar: 'electricity',
    a: 'electricity', vll: 'digital', vln: 'digital', 'ft³': 'digital', 'L': 'digital',
    pf: 'digital', unblv: 'digital', unbli: 'digital', thdva: 'digital', thdi: 'digital',
    thdv: 'digital', othdv: 'digital', othdi: 'digital', ethdv: 'digital', ethdi: 'digital',
    thffv: 'digital', cfv: 'digital',
};

const ACTUATOR_STATUS = { '0': 'Closed', '1': 'Half', '2': 'Partial', '3': 'Open' };

// Alerts / maintenance / actuator are drawn as marker-only "event" series.
const EVENT_SERIES_CONFIG = {
    alerts: { label: 'Alerts', internalId: 'alert', color: '#F75D5F', lookupKey: 'data2' },
    maintenance: { label: 'Maintenance', internalId: 'maintenance', color: '#F75D5F', lookupKey: 'data3' },
    actuator: { label: 'Actuator Status', internalId: 'actuator', color: '#F9941E', lookupKey: 'data4' },
};
const EVENT_INTERNAL_IDS = Object.values(EVENT_SERIES_CONFIG).map((c) => c.internalId);

// Alerts are split into one line per alert type; every type sits at the same height (1 = alert, null = none).
// Colors are handed out in this order and the same type gets the same color on every channel.
// The hues are spaced apart (blue, orange, aqua, yellow, magenta, green, violet, red) and the order
// keeps neighbouring colors distinguishable for colour-blind users. A 9th type starts over at blue.
const ALERT_TYPE_COLORS = ['#e34948', '#eb6834', '#e87ba4', '#4a3aa7','#2a78d6', '#1baf7a', '#eda100', '#008300',  ];
const UNKNOWN_ALERT_TYPE = 'Other';

const GAS_BUTTON_ON = '#FDB23E';
const GAS_BUTTON_OFF = '#007B4E';

// Sidebar checkboxes: [visibility key, label]
const CONSUMPTION_TOGGLES = [
    ['kw', 'Real Power (kW)'],
    ['kva', 'Apparent Power (kVA)'],
    ['kvar', 'Reactive Power (kVAr)'],
    ['a', 'Current (A)'],
];
const POWER_QUALITY_TOGGLES = [
    ['pf', 'Power Factor'],
    ['vll', 'Voltage (V-L-L)'],
    ['vln', 'Voltage (V-L-N)'],
];

// Harmonics dropdown. view: 'phase' = only in Phase view, 'circuit' = only in Circuit view, 'both' = always.
const HARMONIC_OPTIONS = [
    { value: 'thdv', label: 'THD (V)', view: 'phase' },
    { value: 'othdv', label: 'OTHD (V)', view: 'phase' },
    { value: 'othdi', label: 'OTHD (I)', view: 'phase' },
    { value: 'ethdv', label: 'ETHD (V)', view: 'phase' },
    { value: 'ethdi', label: 'ETHD (I)', view: 'phase' },
    { value: 'thffv', label: 'THFF (V)', view: 'phase' },
    { value: 'cfv', label: 'CF (V)', view: 'phase' },
    { value: 'unblv', label: 'Unbl (V)', view: 'circuit' },
    { value: 'unbli', label: 'Unbl (I)', view: 'circuit' },
    { value: 'thdva', label: 'THD (V)', view: 'circuit' },
    // { value: 'kfi', label: 'KF (I)', view: 'both' },
    { value: 'thdi', label: 'THD (I)', view: 'both' },
];


/* ==========================================================================
   GENERIC HELPERS
   ========================================================================== */

const deepClone = (value) => JSON.parse(JSON.stringify(value));
const shallowCopy = (value) => ({ ...value });

// True for regular data series (not alerts / maintenance / actuator).
const isPlainSeries = (obj) => !obj.alerts && !obj.maintenance && !obj.actuator;

const logFetchError = (e) => {
    if (e.message === 'cancelling') return;
    console.log(e);
};

function formatAxisLabel() {
    return this.value.toLocaleString();
}


/* ==========================================================================
   DATE / INTERVAL HELPERS
   ========================================================================== */

// Default data interval for a date range: 1 min, 15 min, 60 min or 'day'.
const getAutoDetail = ([start, end]) => {
    const diff = GraphFuncs.getDifferenceInDays(start, end);
    if (diff > 1 && diff < 31) return 15;
    if (diff >= 30 && diff <= 89) return 60;
    if (diff >= 90) return 'day';
    return 1;
};

// Note: only the string "60" (set by the dropdown) switches to energy units, as before.
const getUnitLabels = (detail) => {
    const isEnergy = ['60', 'day', 'month'].includes(detail);
    return {
        pf: ' (PF)',
        kw: isEnergy ? 'in kWh' : 'in kW',
        vll: 'in V (L-L)',
        vln: 'in V (L-N)',
        kva: isEnergy ? 'in kVAh' : 'in kVA',
        a: 'in A',
        kvar: isEnergy ? 'in kVArh' : 'in kVAr',
        L: 'in L',
        'ft³': 'in ft³',
        co: 'in t CO₂e',
    };
};

const getPointInterval = (detail) => {
    if (detail === 'day') return DAY_MS;
    if (detail === 'month') return null;
    return detail * 60 * 1000; // detail is in minutes
};

// Electricity emissions are always hourly unless viewing daily/monthly.
const getSeriesPointInterval = (detail, isElectricEmissions) => {
    if (detail === 'month') return null;
    if (isElectricEmissions && detail !== 'day') return HOUR_MS;
    return getPointInterval(detail);
};

const withMonthlyInterval = (seriesOptions, detail) => {
    if (detail === 'month') seriesOptions.pointIntervalUnit = 'month';
    return seriesOptions;
};

function parseDateString(dateString, start) {
    const parts = dateString.split('-');
    const [year, month, day] = [parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])];
    return start
        ? Date.UTC(year, month, day, 0, 0, 0)
        : Date.UTC(year, month, day, 23, 59, 59);
}

const generateTimestamps = (startDate, endDate, step) => {
    const timestamps = [];
    const currentDate = moment(startDate, 'YYYY-MM-DD').set({ hour: 0, minute: 0, second: 0 });
    let endDateObj = moment(endDate, 'YYYY-MM-DD').set({ hour: 23, minute: 59, second: 0 });

    // Adjust endDateObj if it falls on today's date
    const today = moment().startOf('day');
    if (endDateObj.isSame(today, 'day')) {
        endDateObj = today.clone().endOf('day');
    }

    const timeIncrement = step === 'day' ? { amount: 1, unit: 'days' }
        : step === 'month' ? { amount: 31, unit: 'days' }
            : { amount: parseInt(step), unit: 'minutes' };

    while (currentDate.isSameOrBefore(endDateObj)) {
        if (step === 'month') currentDate.startOf('month');
        timestamps.push(currentDate.format('YYYY-MM-DD HH:mm:ss'));
        currentDate.add(timeIncrement.amount, timeIncrement.unit);
    }
    return timestamps;
};


/* ==========================================================================
   Y-AXIS TICK HELPERS
   ========================================================================== */

const replaceWithFirstDigit = (number) => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(number))));
    const firstDigit = Math.floor(Math.abs(number) / magnitude);
    return parseInt(firstDigit * magnitude) * Math.sign(number);
};

const findLargestDigitsNumber = (numbers) => {
    const maxDigits = Math.max(...numbers.map((num) => String(num * Math.sign(num)).length));
    let filteredNumbers = numbers.filter((num) => String(num).length === maxDigits);
    if (filteredNumbers.length > 1) {
        if (!filteredNumbers.includes(0)) {
            filteredNumbers.unshift(0);
            filteredNumbers = filteredNumbers.sort((a, b) => a - b);
        }
        return filteredNumbers;
    }
    return numbers;
};

const generateArrayFromNumber = (min, max) => {
    min *= 1.8;
    max *= 1.8;
    const number = max - min;
    if (!(number && (min || max))) return [0, 10];

    const step = number / 4;
    const result = [];
    for (let i = Math.ceil(min); i <= Math.ceil(max); i += Math.ceil(step)) {
        result.push(i);
    }
    let final = new Set();
    result.forEach((e) => final.add(e === 0 ? 0 : replaceWithFirstDigit(e)));
    final = [...final];
    final.push(final[final.length - 1] + (final[final.length - 1] - final[final.length - 2]));
    return findLargestDigitsNumber(final);
};


/* ==========================================================================
   SERIES DATA BUILDERS
   ========================================================================== */

// Values for event series (alerts / maintenance / actuator), aligned to timestamps.
const buildEventValues = (seriesMap, timestamps, useDateOnlyKeys) => timestamps.map((timestamp) => {
    const key = useDateOnlyKeys ? timestamp.split(' ')[0] : timestamp;
    return key in seriesMap && seriesMap[key] !== null ? seriesMap[key] : null;
});

// Temperature values, aligned to timestamps; gaps are filled with the last known value.
const buildTemperatureValues = (seriesMap, timestamps, useDateOnlyKeys) => {
    let last = null;
    return timestamps.map((timestamp) => {
        const key = useDateOnlyKeys ? timestamp.split(' ')[0] : timestamp;
        if (key in seriesMap && seriesMap[key] !== null) {
            last = parseFloat(seriesMap[key].toFixed(2));
        }
        return last;
    });
};

// Converts the temperature API readings into { timestamp: temp }, averaged per month if needed.
const buildTemperatureSeries = (readings, detail) => {
    const series = {};
    for (const key in readings) {
        if (detail !== 'month') {
            series[key] = readings[key].temp;
        } else {
            const monthKey = moment(key).startOf('month').format('YYYY-MM-DD');
            series[monthKey] = series[monthKey] || [];
            series[monthKey].push(readings[key].temp);
        }
    }
    if (detail !== 'month') return series;
    return Object.fromEntries(
        Object.entries(series).map(([k, v]) => [k, v.reduce((a, b) => a + b) / v.length])
    );
};

/**
 * Decides which legend line an alert belongs to.
 * >>> This is the only place to change if the alert type comes from somewhere else. <<<
 * Objects: uses their `type` / `alert_type` field. Strings: the whole text is the type.
 */
const getAlertType = (alert) => {
    if (alert && typeof alert === 'object') return alert.type || alert.alert_type || UNKNOWN_ALERT_TYPE;
    return String(alert).trim() || UNKNOWN_ALERT_TYPE;
};

/**
 * Splits { timestamp: [alert, ...] } into { type: { timestamp: [alert, ...] } }.
 * Types are sorted so the legend order stays stable.
 */
const splitAlertsByType = (seriesMap) => {
    const byType = {};
    for (const [timestamp, alerts] of Object.entries(seriesMap || {})) {
        if (alerts === null || alerts === undefined) continue;
        for (const alert of [].concat(alerts)) {
            const type = getAlertType(alert);
            byType[type] = byType[type] || {};
            byType[type][timestamp] = byType[type][timestamp] || [];
            byType[type][timestamp].push(alert);
        }
    }
    return Object.fromEntries(Object.keys(byType).sort().map((type) => [type, byType[type]]));
};

const mapActuatorStatuses = (seriesMap) => Object.fromEntries(
    Object.entries(seriesMap).map(([key, value]) => [key, ACTUATOR_STATUS[value]])
);

const getEventKind = (series) => {
    if (series.alerts) return 'alerts';
    if (series.maintenance) return 'maintenance';
    if (series.actuator) return 'actuator';
    return null;
};

/**
 * `overrides` (optional) lets one event series be split into several lines:
 *  - seriesMap: the subset of series.series to draw
 *  - label / color: legend text and color
 *  - alertType: shown as the tooltip heading
 */
const buildEventSeries = (series, kind, { timestamps, detail, pointStart, yAxis }, overrides = {}) => {
    const config = EVENT_SERIES_CONFIG[kind];
    const { internalId, lookupKey } = config;
    const label = overrides.label || config.label;
    const color = overrides.color || config.color;
    const seriesMap = overrides.seriesMap || series.series;
    const isActuator = kind === 'actuator';
    const values = buildEventValues(seriesMap, timestamps, isActuator && DAILY_DETAILS.includes(detail));

    return withMonthlyInterval({
        data: isActuator ? values : values.map((e) => e && 1),
        // Raw lookup used by the tooltip (data2 = alerts, data3 = maintenance, data4 = actuator)
        [lookupKey]: isActuator ? mapActuatorStatuses(seriesMap) : seriesMap,
        ...(overrides.alertType && { alertType: overrides.alertType }),
        visible: true,
        pointStart,
        marker: { enabled: true, radius: 4, symbol: 'circle', fillColor: color, lineWidth: 0, lineColor: color },
        pointInterval: getPointInterval(detail),
        color,
        name: `${series.channel_name} - ${label}`,
        // The chart stacks series that share an axis. Each event line gets its own stack so
        // simultaneous alerts / maintenance / actuator values are not added together (1 + 1 = 2).
        stack: `event: ${series.channel_name} - ${label}`,
        type: 'line',
        yAxis,
        xAxis: 0,
        internalId,
        toggle_checked: true,
        comparison: series.compare,
        zIndex: 0,
    }, detail);
};


/* ==========================================================================
   CHART OPTION FACTORIES
   ========================================================================== */

const TOOLTIP_HEADER_STYLE = 'border-bottom: 1px solid #ccc;margin-bottom: 5px; padding-bottom: 5px;font-size:12px';
const TOOLTIP_ROW_STYLE = 'color:black;font-size: 12px; padding-bottom: 5px';
const TOOLTIP_TITLE_STYLE = 'color:black;font-size: 12px; padding-bottom: 5px;font-weight: 700';

function tooltipFormatter() {
    let content = `<div style="${TOOLTIP_HEADER_STYLE}">Time Stamp: <b>${Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', this.x)}</b></div>`;
    if (!this.points) return content;

    const addEventList = (title, entries) => {
        content += `<div style="${TOOLTIP_TITLE_STYLE}">${title}${entries ? ":" : ""}</div>`;
        if (entries) entries.forEach((entry) => content += `<div style="${TOOLTIP_ROW_STYLE}">${entry}</div>`);
    };

    this.points.forEach((point) => {
        const { data2, data3, data4, alertType } = point.series.options;

        if (!data2 && !data3 && !data4) {
            const [name, unit = ''] = point.series.name.split(' in ');
            content += `<div style="color:${point.color};font-size: 12px; padding-bottom: 5px">${name}: <b>${point.y.toLocaleString()} ${unit}</b></div>`;
            return;
        }

        const key = moment(point.x).utc().format('YYYY-MM-DD HH:mm:ss');
        if (data2) addEventList(alertType ? alertType : 'Unknown Alert');
        if (data3) addEventList('Maintenance', data3[key]);
        if (data4) content += `<div style="${TOOLTIP_TITLE_STYLE}">Actuator: ${data4[key]}</div>`;
    });
    return content;
}

const createInitialOptions = () => ({
    chart: {
        type: 'areaspline',
        zoomType: 'x',
    },
    credits: { enabled: false },
    exporting: {
        buttons: {
            contextButton: {
                menuItems: ['viewFullscreen', 'separator', 'downloadPNG', 'downloadPDF', 'downloadCSV'],
            },
        },
        sourceWidth: 1500,
        sourceHeight: 400,
        scale: 1,
        enabled: true,
    },
    navigation: {
        buttonOptions: { align: 'right', verticalAlign: 'bottom', y: 0 },
    },
    navigator: { enabled: false },
    tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderWidth: 1,
        borderColor: '#ccc',
        formatter: tooltipFormatter,
        positioner: () => ({ x: 50, y: 0 }),
    },
    legend: {
        enabled: true,
        align: 'left',
        layout: 'horizontal',
        verticalAlign: 'bottom',
        itemMarginBottom: 0,
        itemMarginTop: 0,
    },
    rangeSelector: { enabled: false },
    backgroundColor: 'transparent',
    series: [],
    plotOptions: {
        series: {
            stacking: 'overlap',
            dataGrouping: { enabled: false },
            lineWidth: 1.5,
            color: '#cccccc',
        },
    },
    accessibility: { enabled: false },
    yAxis: [],
    xAxis: [{
        type: 'datetime',
        minTickInterval: HOUR_MS,
        minRange: 60 * 1000,
        tickPixelInterval: 100,
        ordinal: false,
        crosshair: true,
        visible: true,
        min: null,
        max: null,
        internalType: 'main',
        dateTimeLabelFormats: { second: '%d %b %Y <br/> %H:%M:%S %P' },
        zoomEnabled: true,
    }],
});

const createCompareXAxis = ([compareStart, compareEnd]) => ({
    type: 'datetime',
    minTickInterval: HOUR_MS,
    minRange: DAY_MS,
    ordinal: false,
    crosshair: true,
    dateTimeLabelFormats: { second: '%d %b %Y <br/> %H:%M:%S %P' },
    visible: true,
    internalType: 'compare',
    zoomEnabled: true,
    min: parseDateString(compareStart, true),
    max: parseDateString(compareEnd, false),
});

// Hidden per-series y-axis. Ranges are filled in later by scaleSeriesAxes.
const createSeriesAxis = (unit, labelAlign) => ({
    lineColor: '#709982',
    gridLineWidth: 0,
    lineWidth: 0.1,
    tickWidth: 0.1,
    opposite: false,
    visible: false,
    unit,
    min: null,
    max: null,
    type: 'linear',
    title: {
        align: 'high',
        offset: 0,
        rotation: 0,
        y: 10,
        text: UNIT_AXIS_GROUP[unit] === 'digital' ? 'Digital / Power Quality' : 'Consumption',
    },
    labels: {
        align: labelAlign,
        style: { color: '#85929E ' },
        formatter: formatAxisLabel,
    },
});

const createEventAxis = (unit) => ({
    lineColor: '#F75D5F',
    gridLineWidth: 0,
    lineWidth: 0.1,
    tickWidth: 0.1,
    opposite: true,
    visible: false,
    alertAxis: true,
    unit,
    min: 0,
    max: 3,
    type: 'linear',
    labels: {
        align: 'left',
        style: { color: '#85929E ' },
        formatter: formatAxisLabel,
    },
});

const createTemperatureAxis = (unit, unitTemp, visible) => ({
    lineColor: '#709982',
    gridLineWidth: 0,
    lineWidth: 0.1,
    tickWidth: 0.1,
    opposite: true,
    visible,
    unit,
    min: -40,
    max: unitTemp === 'c' ? 40 : 112,
    type: 'linear',
    title: {
        align: 'high',
        text: 'Temperature',
        offset: 0,
        rotation: 0,
        y: 10,
    },
    labels: {
        align: 'left',
        style: { color: '#85929E ' },
        formatter: formatAxisLabel,
    },
});

// Makes one of the shared (visible) axes ready to be added to the chart.
const showSharedAxis = (axis) => {
    axis.visible = true;
    axis.tickPositions = generateArrayFromNumber(axis.min, axis.max);
    axis.labels = { formatter: formatAxisLabel };
    return axis;
};

/**
 * Applies min/max/ticks to every per-series axis and builds the two shared axes:
 *  - consumptionAxis: first "electricity" group axis (left)
 *  - qualityAxis: first "digital" group axis (right)
 * Mutates `axes`.
 */
const scaleSeriesAxes = (axes, maxByKey, minByKey) => {
    let consumptionAxis = null;
    let qualityAxis = null;
    let onlyPf = true;

    for (const axis of axes) {
        if (axis.unit === 'co') {
            axis.max = Math.ceil(maxByKey[axis.unit]);
            continue;
        }
        if (!(axis.unit in UNIT_AXIS_GROUP)) {
            axis.max = Math.ceil(maxByKey[axis.unit]);
            axis.min = Math.ceil(minByKey[axis.unit]);
            continue;
        }

        const group = UNIT_AXIS_GROUP[axis.unit];
        if (group === 'digital') {
            if (axis.unit === 'pf') axis.max = 1;
            else onlyPf = false;
            axis.opposite = true;
            axis.labels.align = 'left';
            if (!qualityAxis) {
                qualityAxis = deepClone(axis);
                qualityAxis.max = Math.ceil(maxByKey[group]);
                qualityAxis.min = Math.ceil(minByKey[group]);
            }
        } else if (group === 'electricity') {
            if (!consumptionAxis) {
                consumptionAxis = deepClone(axis);
                consumptionAxis.max = Math.ceil(maxByKey[group]);
                consumptionAxis.min = Math.ceil(minByKey[group]);
            }
        }

        if (axis.unit === 'pf') {
            axis.max = 1;
        } else {
            axis.max = Math.ceil(maxByKey[group]);
            axis.min = Math.ceil(minByKey[group]);
        }
        axis.tickPositions = generateArrayFromNumber(axis.min, axis.max);
    }

    // Power factor alone is capped at 1; mixed with other quality units it shares their range.
    if (qualityAxis && onlyPf) {
        qualityAxis.max = 1;
    } else if (qualityAxis) {
        const pfAxis = axes.find((axis) => axis.unit === 'pf');
        if (pfAxis) {
            const group = UNIT_AXIS_GROUP[pfAxis.unit];
            pfAxis.max = Math.ceil(maxByKey[group]);
            pfAxis.min = Math.ceil(minByKey[group]);
            pfAxis.tickPositions = generateArrayFromNumber(pfAxis.min, pfAxis.max);
        }
    }

    return { consumptionAxis, qualityAxis };
};


/* ==========================================================================
   COMPONENT
   ========================================================================== */

export default function MainGraph(props) {

    const context = useContext(AppContext);

    /* ---------- State ---------- */

    const [unitTemp, setUnitTemp] = useState('c');
    const [visible, setVisible] = useState(() => ({
        kw: true, kva: false, kvar: false, a: false, water: false, gas: false, co: false, outdoortemp: false,
        alerts: props.origin === 'alerts',
        actuator: false,
        maintenance: props.origin === 'maintenance',
        pf: false, vll: false, vln: false, phases: false, harmonics: false,
        ...HARMONICS_OFF,
    }));
    const [startEnd, setStartEnd] = useState(() => props.startEnd || [moment().subtract(1, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]);
    const [compareStartEnd, setCompareStartEnd] = useState(null);
    const [compareOn, setCompareOn] = useState(false);
    const [update, setUpdate] = useState(true);
    const [options, setOptions] = useState(createInitialOptions);
    const [graphSeries, setGraphSeries] = useState([]); // requests to fetch (or cached series to reuse)
    const [graphData, setGraphData] = useState([]);     // fetched series
    const [harmonicsUnit, setHarmonicsUnit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(true);
    const [eDetail, setEDetail] = useState(() => getAutoDetail(startEnd));

    const memoizedCircuits = useMemo(() => graphData.filter((e) => e && isPlainSeries(e)), [graphData]);

    /* ---------- Refs ---------- */

    const chartRef = useRef();
    const naturalGasUser = useRef();
    const fixPeriod = useRef();
    const prevStartEnd = useRef();
    const prevDetail = useRef();
    const dChange = useRef(); // true when cached series must be ignored (date range / interval changed)

    const getDetail = () => getAutoDetail(startEnd);


    /* ---------- Building the list of series to request ---------- */

    /**
     * Returns the list of series the chart needs. A series already in graphData is
     * reused when possible; otherwise a request object is created for get_mixed.
     *  - dateChange: ignore cached main-period series
     *  - compareChange: ignore cached comparison series
     * Returns undefined (and turns the utility on) when a distribution view needs it.
     */
    const checkCircuits = (circuits, dateChange = false, compareChange = false) => {
        const tempGraph = [];
        const hasCompare = compareStartEnd !== null && !dateChange;
        const mainPeriod = { startDate: startEnd[0], endDate: startEnd[1], compare: false };
        const comparePeriod = compareStartEnd && { startDate: compareStartEnd[0], endDate: compareStartEnd[1], compare: true };

        const findCached = (predicate) => graphData.find((obj) => obj && predicate(obj));
        const buildRequest = (circuit, fields) => ({ ...deepClone(circuit), ...fields });
        const pushSeries = (cached, canReuse, createRequest, copy = deepClone) => {
            tempGraph.push(cached && canReuse ? copy(cached) : createRequest());
        };

        for (const circuit of circuits) {
            if (!circuit) continue;
            const isSameChannel = (obj) => obj.channel_name === circuit.channel_name;

            // Alerts & maintenance
            for (const flag of ['alerts', 'maintenance']) {
                if (!visible[flag]) continue;
                pushSeries(
                    findCached((obj) => obj[flag] && isSameChannel(obj) && !obj.compare),
                    !dateChange,
                    () => buildRequest(circuit, {
                        [flag]: true, ...mainPeriod, channel_id: circuit.id, detail: eDetail, type: EVENT_TYPE_IDS[circuit.type],
                    })
                );
            }

            // Actuator status
            if (visible.actuator) {
                pushSeries(
                    findCached((obj) => obj.actuator && isSameChannel(obj) && !obj.compare),
                    !dateChange,
                    () => buildRequest(circuit, { actuator: true, ...mainPeriod, actuator_id: circuit.actuatorId, detail: eDetail })
                );
            }

            // Electricity: one series per visible unit
            if (circuit.type === 'electricity') {
                for (const unit of ELECTRIC_UNITS) {
                    if (!visible[unit] || (unit === 'co' && !visible.kw)) continue;

                    const matches = (compare) => (obj) =>
                        isSameChannel(obj) && !!obj.compare === compare && obj.unit === unit && isPlainSeries(obj);
                    const createElectricRequest = (period) => {
                        const request = buildRequest(circuit, {
                            unit, ...period, detail: eDetail, type: HARMONIC_UNITS.includes(unit) ? 'harmonics' : 'electricity',
                        });
                        for (const channel of request.channels) channel.data = [];
                        return request;
                    };

                    pushSeries(findCached(matches(false)), !dateChange, () => createElectricRequest(mainPeriod));
                    if (hasCompare) {
                        pushSeries(findCached(matches(true)), !compareChange, () => createElectricRequest(comparePeriod), shallowCopy);
                    }
                }
                continue;
            }

            // Water / gas
            if (props.dist && !visible[circuit.type]) {
                setVisible((prev) => ({ ...prev, [circuit.type]: true }));
                return;
            }

            const matchesDigital = (compare) => (obj) =>
                isSameChannel(obj) && obj.detail === eDetail && obj.unit === circuit.unit && !!obj.compare === compare && isPlainSeries(obj);
            const pushDigitalSeries = (unit) => {
                pushSeries(findCached(matchesDigital(false)), !dateChange,
                    () => buildRequest(circuit, { unit, ...mainPeriod, detail: eDetail }), shallowCopy);
                if (hasCompare) {
                    pushSeries(findCached(matchesDigital(true)), !compareChange,
                        () => buildRequest(circuit, { unit, ...comparePeriod, detail: eDetail }), shallowCopy);
                }
            };

            if (visible[circuit.type]) pushDigitalSeries(DIGITAL_UNIT_LABELS[circuit.input_unit]);
            if (circuit.type === 'gas' && visible[circuit.type] && visible.co) pushDigitalSeries('co');
        }

        // Outdoor temperature (not tied to a circuit)
        if (visible.outdoortemp) {
            tempGraph.push({ unit: unitTemp, ...mainPeriod, type: 'temperature' });
            if (hasCompare) tempGraph.push({ unit: unitTemp, ...comparePeriod, type: 'temperature' });
        }
        return tempGraph;
    };


    /* ---------- Fetching ---------- */

    // Copies request info onto an API response so it can be matched/cached later.
    const withRequestInfo = (data, circuit) => Object.assign(data, {
        startDate: circuit.startDate,
        endDate: circuit.endDate,
        type: circuit.type,
        unit: circuit.unit,
        compare: circuit.compare,
        channel_name: circuit.channel_name,
    });

    // Returns a promise resolving to the series for one request (or null if nothing to fetch).
    const fetchSeries = (circuit, cancelToken) => {
        if (circuit.series) return Promise.resolve(circuit); // already fetched

        const { startDate, endDate, id } = circuit;

        if (circuit.alerts) {
            return API.getAlertSeries(cancelToken, startDate, endDate, circuit.channel_id, circuit.detail, circuit.type).then((res) => {
                circuit.series = res.data.series;
                circuit.channel_name = res.data.channel_name;
                return circuit;
            }).catch(logFetchError);
        }

        if (circuit.maintenance) {
            return API.getMaintenanceSeries(cancelToken, startDate, endDate, circuit.channel_id, circuit.detail).then((res) => {
                circuit.series = res.data.series;
                circuit.channel_name = res.data.channel_name;
                return circuit;
            }).catch(logFetchError);
        }

        if (circuit.actuator) {
            return API.getActuatorSeries(cancelToken, startDate, endDate, circuit.actuator_id).then((res) => {
                circuit.series = res.data.series;
                circuit.actuator_id = res.data.actuator_id;
                return circuit;
            }).catch(logFetchError);
        }

        if (circuit.unit === 'co') {
            if (circuit.type === 'electricity') {
                return API.getPriceCoElectric(cancelToken, startDate, endDate, id, eDetail < 60 ? 60 : eDetail).then((res) => {
                    circuit.series = res.data.series;
                    circuit.channels = res.data.channels;
                    return circuit;
                }).catch(logFetchError);
            }
            if (circuit.type === 'gas') {
                return API.getPriceCoGas(cancelToken, startDate, endDate, id, eDetail).then((res) => {
                    circuit.series = res.data.series;
                    return circuit;
                }).catch(logFetchError);
            }
            return null;
        }

        if (circuit.type === 'electricity') {
            return API.getElectricity(cancelToken, startDate, endDate, id, eDetail, circuit.unit)
                .then((res) => withRequestInfo(res.data, circuit))
                .catch(logFetchError);
        }

        if (circuit.type === 'harmonics') {
            // Virtual channels have no harmonics: return an empty series.
            if (circuit.virtual) {
                return Promise.resolve({ ...withRequestInfo({}, circuit), series: {}, channels: {} });
            }
            return API.getHarmonics(cancelToken, startDate, endDate, id, eDetail, circuit.unit)
                .then((res) => withRequestInfo(res.data, circuit))
                .catch(logFetchError);
        }

        if (circuit.type === 'temperature') {
            const frequency = DAILY_DETAILS.includes(eDetail) ? 'daily' : 'hourly';
            const today = new Date();
            // Note: month is intentionally not zero-based here (unchanged from original behaviour).
            const [year, month, day] = endDate.split('-');
            const requestEnd = new Date(year, month, day, 0, 0, 0, 0) > today
                ? GlobalFuncs.formatDate(today, 'YYYY-MM-DD')
                : endDate;

            return API.getTemperature(cancelToken, context.getBuildingInfo.building_id, frequency, startDate, requestEnd, circuit.unit).then((res) => {
                Object.assign(res.data, {
                    startDate,
                    endDate,
                    type: circuit.type,
                    unit: res.data.temperature_unit,
                    compare: circuit.compare,
                    channel_name: 'Temperature',
                    series: buildTemperatureSeries(res.data.readings, eDetail),
                });
                return res.data;
            }).catch(logFetchError);
        }

        // Water / gas
        return API.getDigital(cancelToken, startDate, endDate, id, eDetail, circuit.utility_type)
            .then((res) => withRequestInfo(res.data, circuit))
            .catch(logFetchError);
    };

    const get_mixed = async (cancelToken, circuits) => {
        const requests = circuits.map((circuit) => fetchSeries(circuit, cancelToken)).filter(Boolean);
        return Promise.all(requests);
    };


    /* ---------- Effects ---------- */

    // 1. Date range changed -> reset the data interval to the default for that range.
    useEffect(() => {
        if (startEnd !== prevStartEnd.current && getDetail() !== prevDetail.current) {
            prevDetail.current = getDetail();
            setEDetail(getDetail()); // triggers the main effect below
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startEnd]);

    // 2. Refresh button -> re-request everything.
    useEffect(() => {
        if (!props.circuits) return;
        dChange.current = true;
        const tempGraph = checkCircuits(props.circuits, dChange.current, false);
        dChange.current = false;
        setGraphSeries(tempGraph);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [update]);

    // 3. Parent pre-selects a unit.
    useEffect(() => {
        if (props.selectedUnit) setVisible((prev) => {
            let next = { ...prev, [props.selectedUnit]: true };
            if (!['gas', 'water', 'kw'].includes(props.selectedUnit)) next = { ...next, kw: false };
            return next;
        });
    }, [props.selectedUnit]);

    // 4. Main effect: recompute requested series when range, interval, toggles or circuits change.
    useEffect(() => {
        setLoading(true);
        if (!props.circuits) return;

        if (startEnd !== prevStartEnd.current || eDetail !== prevDetail.current) {
            prevStartEnd.current = startEnd;
            prevDetail.current = eDetail;
            dChange.current = true;

            // Changing the range stops any comparison (this effect re-runs once compareOn is false).
            if (compareOn) {
                setCompareOn(false);
                setCompareStartEnd(null);
                return;
            }
        }

        const tempGraph = checkCircuits(props.circuits, dChange.current, false);
        dChange.current = false;
        setGraphSeries(tempGraph);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startEnd, compareOn, props.circuits, eDetail, unitTemp, visible]);

    // 5. Comparison period chosen -> request comparison series.
    useEffect(() => {
        if (!props.circuits || !compareStartEnd) return;
        setLoading(true);
        setGraphSeries(checkCircuits(props.circuits, false, true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compareStartEnd]);

    // 6. Fetch requested series.
    useEffect(() => {
        if (!context.getBuildingInfo || !graphSeries) return;
        if (graphSeries.length === 0) return;
        const cancelToken = API.cancelToken();
        get_mixed(cancelToken, graphSeries).then((data) => setGraphData(data));
        return () => API.cancel(cancelToken);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graphSeries, context.getBuildingInfo]);

    // 7. Build Highcharts options from fetched data.
    useEffect(() => {
        if (graphData.length === 0) return;

        const detail = eDetail;
        const unitLabels = getUnitLabels(eDetail);
        const tempOptions = deepClone(options);
        tempOptions.series = [];
        tempOptions.yAxis = [];

        let colorIndex = 0;
        let countYAxis = 0;
        let hasTemp = false;        // holds the temperature unit when present
        let hasEventSeries = false; // alerts, maintenance or actuator
        const maxYAxis = {};
        const minYAxis = {};

        // Same alert type -> same color on every channel.
        const alertTypeColors = {};
        const getAlertTypeColor = (type) => {
            if (!(type in alertTypeColors)) {
                const index = Object.keys(alertTypeColors).length;
                alertTypeColors[type] = ALERT_TYPE_COLORS[index % ALERT_TYPE_COLORS.length];
            }
            return alertTypeColors[type];
        };

        // Tracks min/max per axis key (kept identical to original behaviour).
        const trackRange = (key, values) => {
            const tempMax = Math.max(...values);
            const tempMin = Math.min(...values);
            maxYAxis[key] = maxYAxis[key] || -100000;
            if (tempMax > maxYAxis[key]) maxYAxis[key] = tempMax;
            minYAxis[key] = minYAxis[key] || 100000;
            if (tempMin < minYAxis[key]) minYAxis[key] = tempMin > 0 ? 0 : tempMin;
        };

        // Values aligned to timestamps; missing power factor readings in the past default to 1.
        const buildReadingValues = (readings, timestamps, unit) => {
            const values = timestamps.map((timestamp) => {
                if (timestamp in readings) {
                    const value = readings[timestamp];
                    return value === null ? null : parseFloat(value.toFixed(2));
                }
                if (unit === 'pf' && !moment.tz(timestamp, context.getBuildingInfo.timezone).isAfter(moment())) return 1;
                return null;
            });
            return unit === 'pf' ? values.map((e) => (e > 1 ? 1 : e)) : values;
        };

        for (const series of graphData) {
            if (!series) continue;

            const pointStart = moment.tz(series.startDate.replaceAll(' ', 'T'), 'Etc/GMT').valueOf();
            const [periodStart, periodEnd] = series.compare && compareStartEnd ? compareStartEnd : startEnd;
            const isElectric = series.type === 'electricity' || series.type === 'harmonics';
            const seriesLabel = series.type === 'harmonics' ? 'Harmonics' : unitLabels[series.unit];
            const compareSuffix = series.compare ? ' Comparison' : '';

            // Alerts / maintenance / actuator
            const eventKind = getEventKind(series);
            if (eventKind) {
                hasEventSeries = true;
                const eventContext = {
                    timestamps: generateTimestamps(periodStart, periodEnd, detail),
                    detail,
                    pointStart,
                    yAxis: countYAxis,
                };
                const alertsByType = eventKind === 'alerts' ? splitAlertsByType(series.series) : {};
                const alertTypes = Object.keys(alertsByType);

                if (alertTypes.length === 0) {
                    // Maintenance, actuator, or a channel with no alerts: a single line, as before.
                    tempOptions.series.push(buildEventSeries(series, eventKind, eventContext));
                } else {
                    // One line per alert type, so each type can be hidden from the legend.
                    for (const type of alertTypes) {
                        tempOptions.series.push(buildEventSeries(series, eventKind, eventContext, {
                            seriesMap: alertsByType[type],
                            label: `Alerts: ${type}`,
                            color: getAlertTypeColor(type),
                            alertType: type,
                        }));
                    }
                }
                continue;
            }

            // Electricity per phase: one line per channel
            if (visible.phases && isElectric) {
                const timestamps = generateTimestamps(periodStart, periodEnd, detail);
                for (const channel of series.channels) {
                    const values = buildReadingValues(channel.series, timestamps, series.unit);
                    tempOptions.series.push(withMonthlyInterval({
                        data: values,
                        visible: true,
                        pointStart,
                        pointInterval: getSeriesPointInterval(detail, series.type === 'electricity' && series.unit === 'co'),
                        color: COLORS[colorIndex],
                        name: `${series.channel_name} - ${channel.phase} ${seriesLabel}${compareSuffix}`,
                        type: 'line',
                        yAxis: countYAxis,
                        xAxis: series.compare ? 1 : 0,
                        internalId: 'electricity',
                        toggle_checked: true,
                        comparison: series.compare,
                        zIndex: 1,
                    }, detail));
                    tempOptions.yAxis.push(createSeriesAxis(series.unit, 'left'));
                    colorIndex++;
                    countYAxis++;
                    trackRange(UNIT_AXIS_GROUP[series.unit], values);
                }
                continue;
            }

            // Outdoor temperature
            if (series.type === 'temperature') {
                hasTemp = series.unit;
                const timestamps = generateTimestamps(periodStart, periodEnd, detail);
                tempOptions.series.push(withMonthlyInterval({
                    data: buildTemperatureValues(series.series, timestamps, DAILY_DETAILS.includes(detail)),
                    visible: true,
                    pointStart,
                    pointInterval: getPointInterval(detail),
                    color: series.compare ? '#0000FF85' : '#a3a7a385',
                    name: `Temperature ${series.unit}${series.compare ? ' - Comparison' : ''}`,
                    type: 'line',
                    yAxis: countYAxis,
                    xAxis: series.compare ? 1 : 0,
                    internalId: series.type,
                    toggle_checked: true,
                    comparison: series.compare,
                    zIndex: 0,
                }, detail));
                continue;
            }

            // Everything else: electricity per circuit, harmonics, water, gas, emissions
            const isElectricEmissions = isElectric && series.unit === 'co';
            const timestampStep = isElectricEmissions && detail < 60 ? 60 : detail;
            const values = buildReadingValues(series.series, generateTimestamps(periodStart, periodEnd, timestampStep), series.unit);
            tempOptions.series.push(withMonthlyInterval({
                data: values,
                visible: true,
                pointStart,
                pointInterval: getSeriesPointInterval(detail, isElectricEmissions),
                color: COLORS[colorIndex],
                name: `${series.channel_name} ${seriesLabel}${compareSuffix}`,
                type: 'line',
                yAxis: countYAxis,
                xAxis: series.compare ? 1 : 0,
                internalId: series.type,
                toggle_checked: true,
                comparison: series.compare,
            }, detail));
            tempOptions.yAxis.push(createSeriesAxis(series.unit, 'right'));
            countYAxis++;
            colorIndex++;
            trackRange(series.unit in UNIT_AXIS_GROUP ? UNIT_AXIS_GROUP[series.unit] : series.unit, values);
        }

        // Shared visible axes (Consumption on the left, Digital / Power Quality on the right)
        const { consumptionAxis, qualityAxis } = scaleSeriesAxes(tempOptions.yAxis, maxYAxis, minYAxis);
        if (consumptionAxis) tempOptions.yAxis.push(showSharedAxis(consumptionAxis));
        if (qualityAxis) tempOptions.yAxis.push(showSharedAxis(qualityAxis));

        // Event axis (or hide markers when there are no event series)
        if (hasEventSeries) {
            tempOptions.yAxis.push(createEventAxis(hasTemp));
            // All event lines (alerts, maintenance, actuator) share the event axis so their markers line up.
            const eventAxisIndex = tempOptions.yAxis.findIndex((axis) => axis.alertAxis);
            tempOptions.series.forEach((s) => {
                if (EVENT_INTERNAL_IDS.includes(s.internalId)) s.yAxis = eventAxisIndex;
            });
        } else {
            tempOptions.series.forEach((s) => { s.marker = { enabled: false }; });
        }

        // Temperature axes (a hidden second one for the comparison period)
        if (hasTemp) {
            tempOptions.yAxis.push(createTemperatureAxis(hasTemp, unitTemp, true));
            const mainTemp = tempOptions.series.find((s) => s.internalId === 'temperature' && !s.comparison);
            if (mainTemp) mainTemp.yAxis = tempOptions.yAxis.length - 1;

            if (compareOn) {
                tempOptions.yAxis.push(createTemperatureAxis(hasTemp, unitTemp, false));
                const compareTemp = tempOptions.series.find((s) => s.internalId === 'temperature' && s.comparison === true);
                if (compareTemp) compareTemp.yAxis = tempOptions.yAxis.length - 1;
            }
        }

        // X axes
        if (compareOn && compareStartEnd) {
            if (!tempOptions.xAxis.find((axis) => axis.internalType === 'compare')) {
                tempOptions.xAxis.push(createCompareXAxis(compareStartEnd));
            }
        } else {
            tempOptions.xAxis = tempOptions.xAxis.filter((axis) => axis.internalType !== 'compare');
            tempOptions.xAxis[0].min = parseDateString(startEnd[0], true);
            tempOptions.xAxis[0].max = parseDateString(startEnd[1], false);
        }

        setOptions(tempOptions);
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graphData]);

    // 8. Turning comparison off clears the comparison period.
    useEffect(() => {
        if (!compareOn) setCompareStartEnd(null);
    }, [compareOn]);


    /* ---------- Handlers ---------- */

    const handleDateChange = (data) => {
        if (!data.value) return;
        const [startDate, endDate] = data.value;
        setStartEnd([GlobalFuncs.formatDate(startDate, 'YYYY-MM-DD'), GlobalFuncs.formatDate(endDate, 'YYYY-MM-DD')]);
        fixPeriod.current.value = '';
    };

    // The comparison period has the same length as the main period.
    const handleDateCompare = (data) => {
        if (!data.value) return;
        const diff = GraphFuncs.getDifferenceInDays(startEnd[0], startEnd[1]) - 1;
        const startDate = data.value;
        const endDate = moment(startDate).add(diff, 'days');
        setCompareStartEnd([moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')]);
    };

    // "View By" dropdown: Day / Month / Year
    const handleDateChangeSelection = (e) => {
        if (chartRef.current) chartRef.current.chart.zoomOut();

        const today = new Date();
        const firstDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const lastDayOfMonth = new Date(firstDayOfNextMonth - 1);
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastYear = new Date();
        lastYear.setDate(lastYear.getDate() - 365);

        const format = (date) => GlobalFuncs.formatDate(date, 'YYYY-MM-DD');
        const value = e.currentTarget.value;
        if (value === 'day') setStartEnd([format(today), format(today)]);
        else if (value === 'month') setStartEnd([format(firstDayOfMonth), format(lastDayOfMonth)]);
        else if (value === 'year') setStartEnd([format(lastYear), format(today)]);
    };

    // Arrows next to "View By": shift the range by one day / month / year (direction = -1 or 1).
    const handleSlideSelection = (direction) => {
        const range = fixPeriod.current.value;
        const shiftDate = {
            day: (date) => date.setDate(date.getDate() + direction),
            month: (date) => date.setMonth(date.getMonth() + direction),
            year: (date) => date.setDate(date.getDate() + 365 * direction),
        }[range];
        if (!shiftDate) return;

        const [startYear, startMonth, startDay] = startEnd[0].split('-');
        const [endYear, endMonth, endDay] = startEnd[1].split('-');
        const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
        const end = new Date(endYear, endMonth - 1, endDay, 0, 0, 0, 0);
        shiftDate(start);
        shiftDate(end);
        setStartEnd([GlobalFuncs.formatDate(start, 'YYYY-MM-DD'), GlobalFuncs.formatDate(end, 'YYYY-MM-DD')]);
    };

    const handleVisible = (event) => {
        const { name, checked } = event.currentTarget;
        if (name === 'harmonics') {
            setHarmonicsUnit('');
            setVisible((prev) => ({ ...prev, [name]: checked, ...HARMONICS_OFF }));
        } else {
            setVisible((prev) => ({ ...prev, [name]: checked }));
        }
    };

    // Only one harmonic unit can be shown at a time.
    const handleHarmonic = (event) => {
        const value = event.currentTarget.value;
        setHarmonicsUnit(value);
        setVisible((prev) => ({ ...prev, ...HARMONICS_OFF, [value]: true }));
    };

    // Circuit / Phase toggle: keep harmonic units valid for the selected view.
    const handleCircuitPhase = (isPhase) => {
        setVisible((prev) => ({
            ...prev,
            phases: isPhase,
            thdv: visible.thdv ? isPhase : false,
            othdv: visible.othdv ? isPhase : false,
            ethdv: visible.ethdv ? isPhase : false,
            thffv: visible.thffv ? isPhase : false,
            cfv: visible.cfv ? isPhase : false,
            unblv: visible.unblv ? !isPhase : false,
            unbli: visible.unbli ? !isPhase : false,
            thdva: visible.thdva ? !isPhase : false,
        }));
    };

    // Natural gas users button (distribution view)
    const handleClick = () => {
        const button = naturalGasUser.current;
        const turnOn = button.getAttribute('data-value') === 'false';
        const color = turnOn ? GAS_BUTTON_ON : GAS_BUTTON_OFF;

        button.style.borderColor = color;
        button.querySelectorAll('path').forEach((path) => path.setAttribute('fill', color));
        props.selectNaturalGasUsers(turnOn);
        button.setAttribute('data-value', turnOn ? 'true' : 'false');
    };


    /* ---------- Render ---------- */

    const autoDetail = getDetail();
    const showSlideArrows = fixPeriod.current && fixPeriod.current.value !== '';

    const renderCheckbox = ([name, label]) => (
        <div key={name} className='flex flex-nowrap items-center'>
            <input type="checkbox" name={name} id={name} checked={visible[name]} onChange={handleVisible} />
            <label className='p-1 pl-2 text-xs' htmlFor={name}>{label}</label>
        </div>
    );

    const harmonicOptions = HARMONIC_OPTIONS.filter((option) =>
        option.view === 'both' || (option.view === 'phase') === Boolean(visible.phases)
    );

    return (
        <div>
            <div className={`${"card tablet:p-8"} rounded-lg relative`}>
                {props.title &&
                    <h2 className="capitalize w-full laptop:text-3xl tablet:text-xl   font-bold text-[#332D41] md:decoration-dashed">{props.title}</h2>
                }
                <div className="flex flex-nowrap tablet:justify-end laptop:justify-start mobile:justify-end rounded-lg min-h-[600px]">

                    {/* ---------- Sidebar ---------- */}
                    <div className={`${open ? 'laptop:w-[20%] tablet:w-1/2 mobile:w-full' : 'w-0'} side-card mr-2 mt-2 flex-col z-10 laptop:static tablet:absolute mobile:absolute tablet:left-0 tablet:top-0 mobile:left-0 mobile:top-0 laptop:min-h-[600px] laptop:h-auto tablet:h-full mobile:h-full backround-white tablet:[card] transition-width duration-300 ease-in-out`}>
                        {open && <div className='flex flex-col justify-between h-full mr-3'>
                            <div>
                                <div className=" grid grid-col justify-self-center">
                                    <h2 className='text-lg font-medium mt-2'>Electricity</h2>

                                    <h3 className='ml-4 mb-2 font-normal mt-2 text-base'>Consumption</h3>
                                    <div className='ml-4 left-green-border pl-2 flex flex-col items-start justify-center'>
                                        {CONSUMPTION_TOGGLES.map(renderCheckbox)}
                                    </div>

                                    <h3 className='ml-4 mb-2 font-normal mt-4 text-base'>Power Quality</h3>
                                    <div className='ml-4 left-green-border pl-2 flex flex-col items-start justify-center mb-2'>
                                        <div className='flex flex-nowrap items-center'>
                                            <input type="checkbox" name="harmonics" id="h" checked={visible.harmonics} onChange={handleVisible} disabled={!context.isGwUser} />
                                            <label className={`p-1 pl-2 text-xs ${!context.isGwUser ? 'text-grey' : ''}`} htmlFor="h">Harmonics</label>
                                            <Tooltip
                                                text="Harmonics not available for virtual channels."
                                                right="0"
                                                left="400%"
                                                top="0"
                                                width="250px"
                                                height="30px"
                                                textAlign="left"
                                                show={true}>
                                                <div className="ml-2">
                                                    {GlobalSVG.info('black', '15', '15')}
                                                </div>
                                            </Tooltip>
                                        </div>
                                        {visible.harmonics &&
                                            <select className='w-1/2 p-2 ml-2 text-xs outline-none' name="harmonics" value={harmonicsUnit} onChange={handleHarmonic}>
                                                <option value="">Select</option>
                                                {harmonicOptions.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                                            </select>
                                        }
                                        {POWER_QUALITY_TOGGLES.map(renderCheckbox)}
                                    </div>

                                    <UnitToggle setOptions={handleCircuitPhase} options={visible.phases} option1={false} option2={true} button1="Circuit" button2="Phase" />
                                    {!props.dist && <SlideToggle setOptions={setVisible} options={visible} id={"water"} index={1} name={"Water"} />}
                                    {!props.dist && <SlideToggle setOptions={setVisible} options={visible} id={"gas"} index={2} name={"Gas"} />}
                                    {props.dist && <div className='h-[10px]'></div>}
                                    <div className='flex flex-nowrap justify-between items-center'>
                                        {props.dist && <p className='text-[#332D41] font-semibold w-[20%]'>Gas</p>}
                                        {props.dist &&
                                            <Tooltip text="Display all electrical circuits that use natural gas”" show={true} top="40px" height="65px" right='-200%'>
                                                <div onClick={handleClick} ref={naturalGasUser} data-value={false} className={`w-[36px] h-[36px] mr-4 flex flex-nowrap justify-center items-center p-[5px] border border-1 border-[#007B4E] rounded-md cursor-pointer`}>
                                                    {GlobalSVG.electricity("#007B4E", 20, 40)}
                                                </div>
                                            </Tooltip>
                                        }
                                    </div>

                                    {["60", 'day'].includes(autoDetail) && <SlideToggle setOptions={setVisible} options={visible} id={"co"} index={3} name={"Emissions"} />}
                                    {context.getUserInfo?.view_alerts && <SlideToggle setOptions={setVisible} options={visible} id={"alerts"} index={5} name={"Alerts"} />}
                                    <SlideToggle setOptions={setVisible} options={visible} id={"maintenance"} index={6} name={"Maintenance"} />
                                    <SlideToggle setOptions={setVisible} options={visible} id={"actuator"} index={6} name={"Actuator Status"} />
                                    <SlideToggle setOptions={setVisible} options={visible} id={"outdoortemp"} index={4} name={"Outdoor Temp"} />
                                    <UnitToggle setOptions={setUnitTemp} options={unitTemp} option1="c" option2="f" button1="&deg;C" button2="&deg;F" />
                                </div>
                            </div>
                            <div className='mt-4 flex flex-nowrap justify-start items-center cursor-pointer' onClick={() => setOpen(false)}>
                                <div className='border border-light-green rounded-full w-8 h-8 flex flex-nowrap items-center justify-center'>
                                    {GlobalSVG.leftArrow()}
                                </div>
                                <p className='ml-3 cursor-pointer'>
                                    Hide options
                                </p>
                            </div>
                        </div>}
                        {!open &&
                            <div className={`${open ? 'w-2/12' : 'items-center'} flex flex-col justify-end h-full`}>
                                <div onClick={() => setOpen(true)} className='cursor-pointer border border-light-green rounded-full tablet:w-8 tablet:h-8 mobile:w-6 mobile:h-6  flex flex-nowrap items-center justify-center'>
                                    {GlobalSVG.rightArrow()}
                                </div>
                            </div>
                        }
                    </div>

                    {/* ---------- Chart area ---------- */}
                    <div className={`col-span-2 ${open ? 'w-[80%]' : 'laptop:w-[100%] mobile:w-[90%]'}`}>
                        <div className="flex flex-wrap justify-end mb-4 items-center">
                            <div className='flex justify-end items-center m-2'>
                                <p className="font-sans font-normal leading-5 text-sans text-[#332D41] md:decoration-dashed w-[100px] text-[14px]">Data Interval:</p>
                                <select className='w-1/2 p-2 ml-2' name="eDetail" value={eDetail} onChange={(e) => setEDetail(e.target.value)}>
                                    {autoDetail <= 1 && <option value="1">1 min</option>}
                                    {autoDetail <= 15 && <option value="15">15 mins</option>}
                                    {autoDetail <= 60 && <option value="60">60 mins</option>}
                                    <option value="day">24h</option>
                                    <option value="month">Month</option>
                                </select>
                            </div>
                            <div className='flex justify-end items-center m-2'>
                                <p className="font-sans font-normal leading-5 text-sans text-[#332D41] md:decoration-dashed text-[14px]">View By:</p>
                            </div>
                            <div className='flex flex-nowrap justify-start items-center m-2'>
                                {showSlideArrows &&
                                    <div className='cursor-pointer' onClick={() => handleSlideSelection(-1)}>
                                        {GlobalSVG.leftArrow()}
                                    </div>}
                                <select ref={fixPeriod} onChange={handleDateChangeSelection} className="outline-none mx-2 p-2 font-sans w-[134px] text-[#007b4e] border-2 border-solid border-neutral-200 rounded-lg text-sm font-bold">
                                    <option value=''>Select</option>
                                    <option value='day'>Day</option>
                                    <option value='month'>Month</option>
                                    <option value='year'>Year</option>
                                </select>
                                {showSlideArrows &&
                                    <div className='cursor-pointer' onClick={() => handleSlideSelection(1)}>
                                        {GlobalSVG.rightArrow()}
                                    </div>}
                            </div>
                            <p className="font-sans font-normal leading-5 text-sans text-[#332D41] md:decoration-dashed m-2"> <b> or </b></p>
                            <div className='flex justify-end items-center m-2'>
                                <DateRangePicker onChange={handleDateChange}
                                    start={startEnd[0]}
                                    end={startEnd[1]}
                                    compare={compareOn}
                                    onChangeCompare={handleDateCompare}
                                    compareStart={compareStartEnd} />
                            </div>
                            {!compareOn && <div className='flex flex-nowrap justify-center items-center m-2 cursor-pointer' onClick={() => setCompareOn(true)}>
                                {GlobalSVG.pentagon()}
                                <p className='ml-2 font-medium text-[#59595C]'>Compare Periods</p>
                            </div>}
                            <div className='cursor-pointer' onClick={() => setUpdate(!update)}>{GlobalSVG.refresh()}</div>
                            {compareOn && <div className='flex flex-nowrap justify-center items-center m-2 cursor-pointer' onClick={() => setCompareOn(false)}>
                                {GlobalSVG.closeBig()}
                                <p className='ml-2 font-medium text-[#59595C]'>Stop Comparison</p>
                            </div>}
                        </div>
                        <HighchartsReact
                            ref={chartRef}
                            highcharts={Highcharts}
                            constructorType={"stockChart"}
                            options={options}
                            style={{ width: '100%' }}
                        />
                        {loading && <Loader />}
                    </div>
                </div>
            </div>
            {props.dist && graphData &&
                <DistribuitionTable circuits={memoizedCircuits} startEnd={startEnd} compare={compareStartEnd} detail={eDetail} />
            }
        </div>
    );
}
